import { addDaysISO } from "./istDate";

/**
 * Body for PATCH /api/customer/subscriptions/{id}/pause.
 *
 * The customer picks the day deliveries should RESUME; the API wants the
 * inclusive pause window `{pause_from, pause_until}`. The pause starts at the
 * earliest date the tenant's cutoff still allows (`earliest_action_date` from
 * GET /api/customer/cutoff-info) and ends the day before the resume date.
 *
 * The old client sent `?days=N` with no body, which the API has never
 * accepted (422) — customer self-pause simply failed.
 *
 * Returns `{ body }` or `{ error }`.
 */
export function buildPauseRequest(earliestActionISO, resumeISO) {
  if (!earliestActionISO || !resumeISO) return { error: "Pick a resume date" };
  const pauseUntil = addDaysISO(resumeISO, -1);
  if (pauseUntil < earliestActionISO) {
    return {
      error: `Deliveries can only be paused from ${earliestActionISO} — pick a resume date after that.`,
    };
  }
  return { body: { pause_from: earliestActionISO, pause_until: pauseUntil } };
}

/** Earliest resume date the picker should allow (the day after the pause can start). */
export function minResumeDate(earliestActionISO) {
  return earliestActionISO ? addDaysISO(earliestActionISO, 1) : "";
}

/** "Paused 31 Oct – 2 Nov · resumes 3 Nov" for a paused subscription.
 *
 *  `pause_until` is the LAST paused day (both window ends are inclusive);
 *  deliveries come back on `resumes_on` (from the server: the day after the
 *  window on which the schedule lands), else the day after `pause_until`.
 *  The screen used to show `pause_until` itself as "Resumes" — a day early.
 */
export function pauseSummary(sub, fmt = (d) => d) {
  if (!sub) return "";
  const from = sub.pause_from, until = sub.pause_until;
  if (!until) return "Paused · until you resume it";
  const back = sub.resumes_on || addDaysISO(until, 1);
  const window = from && from !== until ? `${fmt(from)} – ${fmt(until)}` : fmt(until);
  return `Paused ${window} · resumes ${fmt(back)}`;
}

/** The pause dialog's own summary for a chosen resume date: the days with no
 *  delivery, inclusive. `{ from, until }` or null when nothing is paused. */
export function pauseWindowFor(earliestActionISO, resumeISO) {
  if (!earliestActionISO || !resumeISO) return null;
  const until = addDaysISO(resumeISO, -1);
  return until >= earliestActionISO ? { from: earliestActionISO, until } : null;
}
